/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  input,
  output,
} from '@angular/core';
import {
  PICKUP_MESSAGES,
  SHIPPING_MESSAGES,
} from './product-availability/availability-messaging.constants';

import { FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import { HomefieldProductImageComponent } from '../product-image/product-image.component';
import { IAvailabilityData } from './product-availability/availability-messaging.interface';

@Component({
  selector: 'homefield-product-card',
  templateUrl: './product-card.component.html',
  styleUrl: './product-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class HomefieldProductCardComponent implements OnInit, OnChanges {
  @ViewChild('productCardImage') productCardImage: HomefieldProductImageComponent;
  @Input() productAvailabilityData: IAvailabilityData;
  @Input() promo: string;
  @Input() selectedStoreId: string;
  @Input() chainAbbr = 'dsg';
  @Input() referrer = '';
  @Input() badge: string;
  readonly definingAttributes = input<unknown[] | undefined>(undefined);
  @Input() seoUrl: string;
  @Input() title = '';
  @Input() colors;
  @Input() ecode = '';
  @Input() sku = '';
  @Input() mapPriceIndicator: number;
  @Input() priceIndicator: number;
  @Input() prices: {
    maxlistprice: string;
    maxofferprice: string;
    minlistprice: string;
    minofferprice: string;
  };

  @Input() productQuickViewEnabled;
  @Input() attributes;
  @Input() imageUrl = '';
  @Input() addColorToProductUrl = true;
  @Input() isSponsored = false;
  @Input() isCriteo = false;
  @Input() isUnavailable = false;
  @Input() ratingCount: number | string = 0;
  @Input() ratingValue = 0;
  @Input() showCustomerRating = false;
  @Input() favoritesAttributes = [];
  @Input() inMauiList = false;
  @Input() isDisabled = false;
  @Input() isCustomOrPersonalizedProduct = false;
  @Input() isLCPImage = false;
  @Input() isNgOptimizedImageSupported = false;
  @Input() isComingSoon = false;
  @Input() location;
  @Input() isAntiClearance = false;
  @Input() clickTrackerUrl = '';
  @Input() availabilityOnProductCardsEnabled = true;
  @Input() categoryGroupId = '';
  @Input() categoryIdentifier = '';
  @Input() categoryType = '';
  @Input() oosListEnabled = false;
  @Input() recommendedAnalyticsData;
  imageSwatchesEnabled = input(false);
  sharedList = input<boolean>(false);
  productListID = input<string>('');
  isRecommendedList = input<boolean>(false);

  /**
   * @description Feature flag to display "Sponsored" label at the right
   * bottom of cards for sponsored and Nike exclusive products.
   */
  @Input() showNewSponsoredLabel = false;
  @HostBinding('attr.data-em') @Input() dataEm = '';

  @Output() openAttributesPanelClicked = new EventEmitter();
  @Output() productCardClick = new EventEmitter<string>();
  @Output() ecodeSelected = new EventEmitter<string>();
  openSkuResolvePanelEvent = output<any>();

  readonly hasDefiningAttributes = computed(() => (this.definingAttributes()?.length ?? 0) > 0);

  selectedSwatchColor: string;
  isNikeExclusive = false;
  updatedAssetSeoUrl = '';
  learnMoreUrl = '';
  pdpUrl = '';
  productCardAriaLabel = '';

  protected readonly FavoritesLocationEnum = FavoritesLocationEnum;

  get isOOS(): boolean {
    return (
      this.productAvailabilityData?.onlineInventoryMessage === 'OutOfStock' &&
      this.productAvailabilityData?.inStoreInventoryMessage === 'OutOfStock' &&
      this.oosListEnabled
    );
  }

  ngOnInit(): void {
    this.pdpUrl = this.seoUrl || `/p/${encodeURIComponent(this.title)}-${this.ecode}/${this.ecode}`;

    // TODO: Move this check out of card once pla is using state
    if (this.attributes) {
      this.isNikeExclusive = JSON.parse(this.attributes).find(
        (attribute) => attribute['5632'] && attribute['5632'] === 'Nike APE Exclusive',
      );
    }
    if (this.isNikeExclusive) {
      this.badge = 'Nike APE Exclusive';
    }

    // Set referrer as queryParam for PLA
    if (this.referrer && this.referrer === 'pla') {
      this.pdpUrl = `${this.pdpUrl}?recid=PLA`;
    }

    if (this.chainAbbr !== 'dsg' && this.badge?.toLowerCase() === 'dicksexclusive') {
      this.badge = '';
    }

    // Check to see if we need to add categoryId or Clearance to the URL
    if (
      this.categoryType === 'S' ||
      (this.categoryIdentifier && this.categoryIdentifier.lastIndexOf('SKU-', 0) === 0)
    ) {
      this.pdpUrl = this.addQueryParam(this.pdpUrl, 'categoryId', this.categoryGroupId);
    } else if (this.categoryIdentifier && this.categoryIdentifier.indexOf('Clearance') === 0) {
      this.pdpUrl = this.addQueryParam(this.pdpUrl, 'Clearance', 'true');
    }

    if (this.colors?.length) {
      this.selectedSwatchColor = this.colors[0];
    }

    this.addParamsToUrls();
    this.productCardAriaLabel = this.buildProductCardAriaLabel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.productAvailabilityData?.currentValue || changes?.promo?.currentValue) {
      this.productCardAriaLabel = this.buildProductCardAriaLabel();
    }

    if (changes?.sku?.currentValue && !changes?.sku?.firstChange) {
      this.selectedSwatchColor = this.colors?.length ? this.colors[0] : this.selectedSwatchColor;
    }
  }

  openQuickview(): void {
    // redirect athlete to pdp if product is customized or personalized due to quickview not supporting those products.
    if (this.isCustomOrPersonalizedProduct) {
      window.location.href = this.pdpUrl;
      return;
    }

    const shouldUseCategoryGroupId =
      this.categoryType === 'S' ||
      (this.categoryIdentifier && this.categoryIdentifier.lastIndexOf('SKU-', 0) === 0);

    let productQuickViewParams = {
      clearance: this.categoryIdentifier && this.categoryIdentifier.indexOf('Clearance') === 0,
      skuCategory: shouldUseCategoryGroupId && this.categoryGroupId,
      preview: '',
      skuPartNumber: this.sku,
      defaultColor: this.selectedSwatchColor,
    };

    if (this.recommendedAnalyticsData) {
      productQuickViewParams = {
        ...productQuickViewParams,
        additionalData: this.recommendedAnalyticsData,
      } as any;
    }

    window.quickview.openProductQuickView(this.ecode, this.referrer, productQuickViewParams);
  }

  colorSwatchChanged(newColor: string): void {
    // Only fire if the color changes, otherwise this would fire on hover everytime
    if (this.selectedSwatchColor !== newColor) {
      this.selectedSwatchColor = newColor;
      this.addParamsToUrls();
    }
  }

  openAttributesPanel($event) {
    this.openAttributesPanelClicked.emit($event);
  }

  productCardClicked(): void {
    this.productCardClick.emit(this.updatedAssetSeoUrl);
  }

  openSkuResolvePanel(): void {
    this.openSkuResolvePanelEvent.emit({
      ecode: this.ecode,
      referrer: this.referrer,
      productListID: this.productListID(),
    });
  }

  private addParamsToUrls(): void {
    if (!this.pdpUrl) return;

    // Check to see if we need to add color swatch to URL
    if (this.addColorToProductUrl && this.selectedSwatchColor) {
      this.updatedAssetSeoUrl = this.addQueryParam(this.pdpUrl, 'color', this.selectedSwatchColor);
    } else {
      this.updatedAssetSeoUrl = this.pdpUrl;
    }

    if (this.isNikeExclusive) {
      this.learnMoreUrl = this.pdpUrl;

      if (this.referrer === 'pla' && !this.learnMoreUrl.includes('?recid=PLA')) {
        this.learnMoreUrl = `${this.learnMoreUrl}?recid=PLA`;
      }

      if (this.addColorToProductUrl && this.selectedSwatchColor) {
        this.learnMoreUrl = this.addQueryParam(
          this.learnMoreUrl,
          'color',
          this.selectedSwatchColor,
        );
      }
    }

    if (this.chainAbbr === 'dsg' && this.isAntiClearance === true) {
      this.updatedAssetSeoUrl = this.addQueryParam(
        this.updatedAssetSeoUrl,
        'antiClearance',
        'true',
      );
    }
  }

  private addQueryParam(url: string, param: string, value: string): string {
    const connector = url.includes('?') ? '&' : '?';
    return `${url}${connector}${param}=${value}`;
  }

  trackByFn(index: number, _item: any): number {
    return index;
  }

  public ecodeSelectedEmitter(): void {
    this.ecodeSelected.emit(this.ecode);
  }

  private buildProductCardAriaLabel(): string {
    let productCardAriaLabel = `${this.title}`;

    const productCardAriaLabelDescriptions = {
      pricing: this.getPricingString() || '',
      reviewStars: this.getReviewStarsAriaLabel() || '',
      promo: this.promo || '',
      fulfillment: this.getFulfillmentAriaLabel() || '',
    };

    Object.keys(productCardAriaLabelDescriptions).forEach((key) => {
      if (productCardAriaLabelDescriptions[key]) {
        productCardAriaLabel += `, ${productCardAriaLabelDescriptions[key]}`;
      }
    });

    return productCardAriaLabel;
  }

  private getPricingString(): string {
    let newPriceString = '';

    // Full Price - no Price In Cart (PiC) nor previous or 'was' price (WAS)
    if (this.priceIndicator === 1 && this.mapPriceIndicator === 0) {
      newPriceString = `$${this.prices.minofferprice}`;

      if (this.prices.minofferprice !== this.prices.maxofferprice) {
        newPriceString += ` to $${this.prices.maxofferprice}`;
      }
    }

    // Sale Price - WAS price present, no PiC
    if (this.priceIndicator === 0 && this.mapPriceIndicator === 0) {
      let lowerPrice = `New Lower Price: $${this.prices.minofferprice}`;

      if (this.prices.minofferprice !== this.prices.maxofferprice) {
        lowerPrice += ` to $${this.prices.maxofferprice}`;
      }

      let wasPrice = `Previous Price: $${this.prices.minlistprice}`;

      if (this.prices.minlistprice !== this.prices.maxlistprice) {
        wasPrice += ` to $${this.prices.maxlistprice}`;
      }

      newPriceString = `${lowerPrice} , ${wasPrice}`;
    }

    // PiC and WAS
    if (this.mapPriceIndicator === 1) {
      newPriceString = `See Price in Cart. Previous Price: $${this.prices.minlistprice}`;

      if (this.prices.minlistprice !== this.prices.maxlistprice) {
        newPriceString += ` to $${this.prices.maxlistprice}`;
      }
    }

    return newPriceString;
  }

  private getReviewStarsAriaLabel(): string {
    if (!this.showCustomerRating || !this.ratingCount) {
      return '';
    }

    // ratingValue % 1 !== 0 tells if the number has decimals or not
    return `${this.ratingValue % 1 !== 0 ? this.ratingValue.toFixed(1) : this.ratingValue} out of 5 stars based on ${this.ratingCount} reviews`;
  }

  private getFulfillmentAriaLabel(): string {
    let fulfillmentAriaLabel = '';
    let shippingMessage = '';
    let pickupMessage = '';
    if (this.productAvailabilityData) {
      shippingMessage =
        SHIPPING_MESSAGES[this.productAvailabilityData.onlineInventoryMessage]?.text || '';
      pickupMessage =
        PICKUP_MESSAGES[this.productAvailabilityData.inStoreInventoryMessage]?.text || '';
    }

    if (shippingMessage) {
      fulfillmentAriaLabel += shippingMessage;
    }

    if (shippingMessage && pickupMessage) {
      fulfillmentAriaLabel += ', ';
    }

    if (pickupMessage) {
      fulfillmentAriaLabel += `${pickupMessage}`;
    }

    return fulfillmentAriaLabel;
  }
}
